import React from 'react'

import Tag from 'src/components/DataDisplay/Tag/Tag'
import { StepStatus, StepType } from 'src/graphql/types/steps'
import { formatSnakeValue, formatStepType } from 'src/lib/formatters'
import { DEFAULT_STEP_ORDER } from 'src/lib/stepOrder'

const STEP_COLUMN_PREFIX = 'step_'
const CUSTOM_STEP_COLUMN_PREFIX = 'step_custom_'

// The web StepType enum has no CUSTOM member — the API-side enum does, and
// step filter clauses for custom steps need `type: 'CUSTOM'`.
const CUSTOM_STEP_TYPE = 'CUSTOM'

/**
 * All enum step types that can be shown as table columns, in pipeline order.
 * Custom steps get their own per-record columns.
 */
export const STEP_COLUMN_TYPES: StepType[] = [...DEFAULT_STEP_ORDER]

const STEP_STATUS_VARIANTS: Record<string, string> = {
  [StepStatus.PENDING_REVIEW]: 'warning',
  [StepStatus.IN_PROGRESS]: 'secondary',
  [StepStatus.COMPLETED]: 'success',
  [StepStatus.NOT_STARTED]: 'primary',
  [StepStatus.CANCELED]: 'error',
}

/**
 * Generate column key from a step type / custom step id
 * Enum steps -> `step_<TYPE>`, custom steps -> `step_custom_<customStepId>`
 */
export const stepColumnKey = (type: string, customStepId?: string): string => {
  return customStepId
    ? `${CUSTOM_STEP_COLUMN_PREFIX}${customStepId}`
    : `${STEP_COLUMN_PREFIX}${type}`
}

/**
 * Check if a column key represents a step column
 */
export const isStepColumn = (key: string): boolean => {
  return key.startsWith(STEP_COLUMN_PREFIX)
}

/**
 * Extract the step type (and custom step id, if any) from a column key
 */
export const parseStepColumnKey = (
  key: string
): { type: string; customStepId?: string } => {
  if (key.startsWith(CUSTOM_STEP_COLUMN_PREFIX)) {
    return {
      type: CUSTOM_STEP_TYPE,
      customStepId: key.slice(CUSTOM_STEP_COLUMN_PREFIX.length),
    }
  }
  return { type: key.slice(STEP_COLUMN_PREFIX.length) }
}

/**
 * Renders the current status of a single step as a badge.
 * `step` is the latest matching step row for the column, or null when the
 * applicant's pipeline doesn't include this step.
 */
export const StepStatusCell = ({ step }: { step?: { status: string } }) => {
  if (!step) {
    return <div className="text-doubleNickel-gray-400">—</div>
  }
  return (
    <Tag
      size="xs"
      showCloseButton={false}
      variant={STEP_STATUS_VARIANTS[step.status] ?? 'primary'}
    >
      {formatSnakeValue(step.status)}
    </Tag>
  )
}

type CustomStepOption = { customStepId: string; label: string }

type StepRow = {
  type?: string | null
  status: string
  customStepId?: string | null
}

/**
 * Find the current (latest) step row for a column. `steps.items` is ordered
 * createdAt DESC, so the first match is the most recent row for that step.
 */
const findCurrentStep = (
  applicant,
  type: string,
  customStepId?: string
): StepRow | undefined => {
  const items: StepRow[] = applicant?.steps?.items ?? []
  return customStepId
    ? items.find((s) => s.customStepId === customStepId)
    : items.find((s) => s.type === type && !s.customStepId)
}

/**
 * Generates dynamic table column definitions for pipeline steps —
 * one per enum step type plus one per active custom step.
 * All columns are hidden by default.
 */
export const generateStepColumns = (
  stepTypes: string[] = STEP_COLUMN_TYPES,
  customSteps: CustomStepOption[] = []
) => {
  const enumColumns = stepTypes.map((type) => ({
    key: stepColumnKey(type),
    label: formatStepType(type as StepType, true),
    hidden: true, // Hidden by default
    render: (row) => <StepStatusCell step={findCurrentStep(row, type)} />,
  }))

  const customColumns = customSteps.map((customStep) => ({
    key: stepColumnKey(CUSTOM_STEP_TYPE, customStep.customStepId),
    label: customStep.label,
    hidden: true, // Hidden by default
    render: (row) => (
      <StepStatusCell
        step={findCurrentStep(row, CUSTOM_STEP_TYPE, customStep.customStepId)}
      />
    ),
  }))

  return [...enumColumns, ...customColumns]
}

/**
 * Generate default visibility settings for step columns
 * All step columns are hidden by default
 */
export const generateStepVisibility = (
  stepTypes: string[] = STEP_COLUMN_TYPES,
  customSteps: CustomStepOption[] = []
): Record<string, boolean> => {
  const visibility: Record<string, boolean> = {}
  stepTypes.forEach((type) => {
    visibility[stepColumnKey(type)] = false
  })
  customSteps.forEach((customStep) => {
    visibility[stepColumnKey(CUSTOM_STEP_TYPE, customStep.customStepId)] = false
  })
  return visibility
}

/**
 * Generate default order for step columns — enum types first (pipeline
 * order), then custom steps
 */
export const generateStepOrder = (
  stepTypes: string[] = STEP_COLUMN_TYPES,
  customSteps: CustomStepOption[] = []
): string[] => {
  return [
    ...stepTypes.map((type) => stepColumnKey(type)),
    ...customSteps.map((customStep) =>
      stepColumnKey(CUSTOM_STEP_TYPE, customStep.customStepId)
    ),
  ]
}
